/**
 * Single-report thread.
 *
 * Status flow (simplified, 3 states):
 *   - open      → newly submitted (or reporter said "didn't fix")
 *   - addressed → dev marked it; reporter sees pills to confirm or reject
 *   - resolved  → reporter confirmed the fix
 *
 * Permission-aware actions:
 *   - Dev (any user with dev access):
 *     · "Mark as Addressed" button (visible only when status === 'open').
 *     · Delete report (any).
 *   - Reporter (the user who filed the report):
 *     · When status === 'addressed': two pills, "✅ Mark as resolved"
 *       and "❌ This didn't fix my problem" (the latter prompts for a
 *       comment and reverts to 'open').
 *     · Delete own report.
 *
 * Similar reports surface discreetly at the bottom of the thread.
 */

import React from 'react';
import {
  useBugReport,
  useAddBugReportComment,
  useDevMarkAddressed,
  useDevEditReport,
  useDevSetExcludeFromExport,
  useReporterMarkResolved,
  useReporterMarkNotFixed,
  useDeleteBugReport,
  useMarkBugReportRead,
  useFindSimilarBugReports,
  useIsDev,
} from '../hooks/useBugReports.js';
import { effectiveData, hasDevEdits, isExcludedFromExport } from '../reportData.js';

const STATUS_INFO = {
  open:      { label: 'Open',      color: '#ff6961' },
  addressed: { label: 'Addressed', color: '#9b59b6' },
  resolved:  { label: 'Resolved',  color: '#27c95a' },
};

const TAG_COLORS = {
  bug: '#ff6961',
  critical: '#ff3b30',
  feature: '#4a90d9',
  suggestion: '#c9a227',
  interface: '#9b59b6',
  ui: '#9b59b6',
  question: '#888',
};

const safeParse = (s) => {
  if (typeof s !== 'string' || !s) return {};
  try { return JSON.parse(s) || {}; } catch { return {}; }
};

export default function ReportThread({ reportId, onBack, onOpenReport }) {
  const data = useBugReport(reportId);
  const isDev = useIsDev();
  const similar = useFindSimilarBugReports(reportId, 5);
  const addComment = useAddBugReportComment();
  const devMarkAddressed = useDevMarkAddressed();
  const devEditReport = useDevEditReport();
  const devSetExcludeFromExport = useDevSetExcludeFromExport();
  const reporterMarkResolved = useReporterMarkResolved();
  const reporterMarkNotFixed = useReporterMarkNotFixed();
  const deleteReport = useDeleteBugReport();
  const markRead = useMarkBugReportRead();
  const [reply, setReply] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showNotFixed, setShowNotFixed] = React.useState(false);
  const [notFixedBody, setNotFixedBody] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [editDraft, setEditDraft] = React.useState(null);
  const markedRef = React.useRef(false);

  React.useEffect(() => {
    if (!data || !data.report || markedRef.current) return;
    markedRef.current = true;
    markRead({ reportId }).catch(e => console.warn('markRead failed:', e));
  }, [data, reportId, markRead]);

  if (data === undefined) return <div style={{ padding: 16, color: '#888' }}>Loading…</div>;
  if (data === null) {
    return (
      <div style={{ padding: 16, color: '#888' }}>
        Report not found or you don't have permission.
        {onBack && (
          <button onClick={onBack} style={{ marginLeft: 8, color: '#c9a227', background: 'none', border: 'none', cursor: 'pointer' }}>← Back</button>
        )}
      </div>
    );
  }

  const { report, screenshots, comments, reporterDisplayName } = data;
  const parsed = safeParse(report.data);
  const isReporter = !isDev && report.reporterUserId; // best-effort; server is the truth
  // Effective data — dev sees their own edits applied; reporter always
  // sees their original (avoids "why did my report change?" confusion).
  const eff = effectiveData(parsed, isDev ? 'dev' : 'reporter');
  const tags = Array.isArray(eff.tags) ? eff.tags : [];
  const statusInfo = STATUS_INFO[report.status] || { label: report.status, color: '#888' };
  const isEdited = isDev && hasDevEdits(parsed);
  const isExcluded = isExcludedFromExport(parsed);

  const startEdit = () => {
    setEditDraft({
      title: eff.title || '',
      description: eff.description || '',
      tags: Array.isArray(eff.tags) ? [...eff.tags] : [],
      importance: eff.importance ?? null,
      importanceRaw: eff.importanceRaw ?? null,
      importanceScale: eff.importanceScale ?? null,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editDraft) return;
    setBusy(true);
    try {
      // Send only fields that actually changed from the original. Pass
      // null to clear an override back to the reporter's value.
      const args = { reportId };
      if (editDraft.title !== (parsed.title || '')) args.title = editDraft.title;
      if (editDraft.description !== (parsed.description || '')) args.description = editDraft.description;
      const origTags = Array.isArray(parsed.tags) ? parsed.tags : [];
      if (JSON.stringify(editDraft.tags) !== JSON.stringify(origTags)) args.tags = editDraft.tags;
      if (editDraft.importanceRaw !== (parsed.importanceRaw ?? null)) args.importanceRaw = editDraft.importanceRaw;
      if (editDraft.importanceScale !== (parsed.importanceScale ?? null)) args.importanceScale = editDraft.importanceScale;
      if (editDraft.importance !== (parsed.importance ?? null)) args.importance = editDraft.importance;
      await devEditReport(args);
      setEditing(false);
      setEditDraft(null);
    } catch (e) {
      console.error('Edit failed:', e); alert(e?.message || 'Save failed.');
    } finally { setBusy(false); }
  };

  const revertEdits = async () => {
    if (!window.confirm('Revert all dev edits and restore the reporter\'s original?')) return;
    setBusy(true);
    try {
      await devEditReport({
        reportId,
        title: null, description: null, tags: null,
        importance: null, importanceRaw: null, importanceScale: null,
      });
      setEditing(false);
      setEditDraft(null);
    } catch (e) { console.error(e); alert(e?.message || 'Revert failed.'); }
    finally { setBusy(false); }
  };

  const toggleExcludeFromExport = async () => {
    setBusy(true);
    try { await devSetExcludeFromExport({ reportId, exclude: !isExcluded }); }
    catch (e) { console.error(e); alert(e?.message || 'Update failed.'); }
    finally { setBusy(false); }
  };

  const onReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await addComment({ reportId, body: reply.trim() }); setReply(''); }
    catch (e) { console.error('Add comment failed:', e); alert(e?.message || 'Reply failed.'); }
    finally { setBusy(false); }
  };

  const onAddressed = async () => {
    setBusy(true);
    try { await devMarkAddressed({ reportId }); }
    catch (e) { console.error(e); alert(e?.message || 'Update failed.'); }
    finally { setBusy(false); }
  };

  const onResolved = async () => {
    setBusy(true);
    try { await reporterMarkResolved({ reportId }); }
    catch (e) { console.error(e); alert(e?.message || 'Update failed.'); }
    finally { setBusy(false); }
  };

  const onNotFixed = async () => {
    if (!notFixedBody.trim()) {
      alert('Please describe what is still broken.');
      return;
    }
    setBusy(true);
    try {
      await reporterMarkNotFixed({ reportId, body: notFixedBody.trim() });
      setShowNotFixed(false);
      setNotFixedBody('');
    } catch (e) {
      console.error(e); alert(e?.message || 'Update failed.');
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this report? This is permanent.')) return;
    setBusy(true);
    try {
      await deleteReport({ reportId });
      onBack?.();
    } catch (e) { console.error(e); alert(e?.message || 'Delete failed.'); setBusy(false); }
  };

  return (
    <div style={{ color: '#e8d5b7' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 5,
              color: '#888',
              cursor: 'pointer',
              padding: '4px 10px',
              fontSize: '0.74rem',
            }}
          >
            ← Back
          </button>
        )}
        <span
          style={{
            padding: '3px 10px',
            borderRadius: 12,
            background: `${statusInfo.color}22`,
            border: `1px solid ${statusInfo.color}66`,
            color: statusInfo.color,
            fontSize: '0.72rem',
            fontWeight: 600,
          }}
        >
          {statusInfo.label}
        </span>
        {(() => {
          // Importance label, scale-aware. Critical reports show no
          // badge — the "critical" tag pill is the signal. Prefer raw/
          // scale fields when present; fall back to legacy importance.
          if (tags.includes('critical')) return null;
          if (typeof eff.importanceRaw === 'number' && typeof eff.importanceScale === 'number') {
            return <span style={{ fontSize: '0.72rem', color: '#c9a227' }}>★ {eff.importanceRaw}/{eff.importanceScale}</span>;
          }
          if (typeof eff.importance === 'number') {
            return <span style={{ fontSize: '0.72rem', color: '#c9a227' }}>★ {eff.importance}/10</span>;
          }
          return null;
        })()}
        <span style={{ marginLeft: 'auto', fontSize: '0.62rem', color: '#666' }}>
          {report.reportId}
        </span>
        {isDev && !editing && (
          <button
            onClick={startEdit}
            disabled={busy}
            title="Edit this report (overrides reporter's submission for export)"
            style={{
              background: 'none',
              border: '1px solid rgba(74,144,217,0.4)',
              borderRadius: 5,
              color: '#7eb6e8',
              cursor: busy ? 'wait' : 'pointer',
              padding: '4px 10px',
              fontSize: '0.7rem',
            }}
          >
            ✏️ Edit
          </button>
        )}
        {isDev && (
          <button
            onClick={toggleExcludeFromExport}
            disabled={busy}
            title={isExcluded ? 'Currently excluded from export — click to include again' : 'Exclude this report from the bulk export (jokes, duplicates, noise)'}
            style={{
              background: isExcluded ? 'rgba(184,155,219,0.18)' : 'none',
              border: `1px solid ${isExcluded ? '#b89bdb' : 'rgba(184,155,219,0.4)'}`,
              borderRadius: 5,
              color: '#b89bdb',
              cursor: busy ? 'wait' : 'pointer',
              padding: '4px 10px',
              fontSize: '0.7rem',
            }}
          >
            {isExcluded ? '🚫 Excluded' : '🚫 Exclude'}
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={busy}
          title="Delete this report"
          style={{
            background: 'none',
            border: '1px solid rgba(255,105,97,0.3)',
            borderRadius: 5,
            color: '#ff6961',
            cursor: busy ? 'wait' : 'pointer',
            padding: '4px 10px',
            fontSize: '0.7rem',
          }}
        >
          🗑️ Delete
        </button>
      </div>

      {/* Edited / excluded banner — only shown to the dev. */}
      {isDev && (isEdited || isExcluded) && !editing && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap',
          padding: '6px 10px', marginBottom: 10,
          background: 'rgba(74,144,217,0.06)',
          border: '1px dashed rgba(74,144,217,0.3)',
          borderRadius: 5, fontSize: '0.65rem', color: '#7eb6e8',
        }}>
          {isEdited && (
            <span>
              ✏️ <strong>Dev-edited</strong> — the reporter's original is preserved on the source record but not exported.
              <button
                onClick={revertEdits}
                disabled={busy}
                style={{
                  marginLeft: 8, background: 'none', border: 'none',
                  color: '#7eb6e8', textDecoration: 'underline', cursor: busy ? 'wait' : 'pointer',
                  fontSize: '0.62rem', padding: 0,
                }}
              >revert</button>
            </span>
          )}
          {isExcluded && (
            <span style={{ color: '#b89bdb' }}>
              🚫 <strong>Excluded from export</strong> — this report won't appear in bulk exports.
            </span>
          )}
        </div>
      )}

      {!editing ? (
        <>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '1.05rem', color: '#c9a227' }}>
            {eff.title || '(untitled)'}
          </h3>

          {reporterDisplayName && (
            <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 8 }}>
              Filed by <span style={{ color: '#b0a48a', fontWeight: 500 }}>{reporterDisplayName}</span>
              {' · '}{new Date(report.createdAt).toLocaleString()}
            </div>
          )}

          {tags.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
              {tags.map(t => (
                <span key={t} style={{
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontSize: '0.62rem',
                  background: `${TAG_COLORS[t] || '#888'}22`,
                  border: `1px solid ${TAG_COLORS[t] || '#888'}55`,
                  color: TAG_COLORS[t] || '#888',
                }}>{t}</span>
              ))}
            </div>
          )}

          {eff.description && (
            <div style={{
              fontSize: '0.84rem',
              color: '#b0a48a',
              lineHeight: 1.5,
              padding: 12,
              background: 'rgba(0,0,0,0.18)',
              border: '1px solid rgba(201,162,39,0.15)',
              borderRadius: 6,
              marginBottom: 12,
              whiteSpace: 'pre-wrap',
            }}>
              {eff.description}
            </div>
          )}
        </>
      ) : (
        // Inline dev edit mode. Editable title / description / tags /
        // importance. Save patches data.devEdits; the reporter still
        // sees their original on their side.
        <div style={{
          padding: 12,
          background: 'rgba(74,144,217,0.06)',
          border: '1px solid rgba(74,144,217,0.3)',
          borderRadius: 6,
          marginBottom: 12,
        }}>
          <div style={{ fontSize: '0.7rem', color: '#7eb6e8', fontWeight: 600, marginBottom: 8 }}>
            ✏️ Editing as dev — reporter sees their original; export uses your version.
          </div>
          <input
            value={editDraft.title}
            onChange={e => setEditDraft({ ...editDraft, title: e.target.value })}
            placeholder="Title"
            style={{
              width: '100%', padding: '6px 10px', fontSize: '0.92rem',
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(201,162,39,0.25)',
              borderRadius: 5, color: '#e8d5b7', outline: 'none',
              boxSizing: 'border-box', marginBottom: 8,
            }}
          />
          <textarea
            value={editDraft.description}
            onChange={e => setEditDraft({ ...editDraft, description: e.target.value })}
            placeholder="Description"
            rows={4}
            style={{
              width: '100%', padding: '6px 10px', fontSize: '0.85rem',
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(201,162,39,0.25)',
              borderRadius: 5, color: '#e8d5b7', outline: 'none',
              boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit',
              marginBottom: 8,
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
            <span style={{ fontSize: '0.65rem', color: '#888', alignSelf: 'center', marginRight: 4 }}>Tags:</span>
            {['critical', 'bug', 'interface', 'suggestion', 'question', 'feedback'].map(tagId => {
              const sel = editDraft.tags.includes(tagId);
              const color = TAG_COLORS[tagId] || '#888';
              return (
                <button
                  key={tagId}
                  type="button"
                  onClick={() => setEditDraft({
                    ...editDraft,
                    tags: sel ? editDraft.tags.filter(t => t !== tagId) : [...editDraft.tags, tagId],
                  })}
                  style={{
                    padding: '3px 9px', fontSize: '0.65rem', borderRadius: 10,
                    background: sel ? `${color}22` : 'rgba(0,0,0,0.18)',
                    border: `1px solid ${sel ? color : 'rgba(255,255,255,0.1)'}`,
                    color: sel ? color : '#888',
                    cursor: 'pointer', fontWeight: sel ? 600 : 400,
                  }}
                >
                  {tagId}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.65rem', color: '#888' }}>Importance:</span>
            {(() => {
              // Bug-mode 1-5 if `bug` tag selected, else 1-10. Critical
              // reports skip the slider (the tag is the signal).
              if (editDraft.tags.includes('critical')) {
                return <span style={{ fontSize: '0.65rem', color: '#ff3b30' }}>(critical — no slider)</span>;
              }
              const isBug = editDraft.tags.includes('bug');
              const max = isBug ? 5 : 10;
              const cur = editDraft.importanceRaw ?? editDraft.importance ?? Math.ceil(max / 2);
              return (
                <>
                  <input
                    type="range" min="1" max={max} value={cur}
                    onChange={e => {
                      const raw = parseInt(e.target.value, 10);
                      const normalized = isBug ? raw * 2 : raw;
                      setEditDraft({ ...editDraft,
                        importanceRaw: raw,
                        importanceScale: max,
                        importance: normalized,
                      });
                    }}
                    style={{ flex: 1, maxWidth: 200 }}
                  />
                  <span style={{ fontSize: '0.7rem', color: '#c9a227', minWidth: 40 }}>
                    {cur}/{max}
                  </span>
                </>
              );
            })()}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={saveEdit}
              disabled={busy}
              style={{
                padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700,
                background: 'rgba(39,201,90,0.18)',
                border: '1px solid #27c95a',
                borderRadius: 5, color: '#27c95a',
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              ✓ Save
            </button>
            <button
              onClick={() => { setEditing(false); setEditDraft(null); }}
              disabled={busy}
              style={{
                padding: '6px 14px', fontSize: '0.78rem',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 5, color: '#888',
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Cancel
            </button>
            {isEdited && (
              <button
                onClick={revertEdits}
                disabled={busy}
                title="Revert all dev overrides; restore the reporter's original."
                style={{
                  padding: '6px 14px', fontSize: '0.78rem',
                  background: 'rgba(255,105,97,0.10)',
                  border: '1px solid rgba(255,105,97,0.4)',
                  borderRadius: 5, color: '#ff6961',
                  cursor: busy ? 'wait' : 'pointer',
                  marginLeft: 'auto',
                }}
              >
                Revert all edits
              </button>
            )}
          </div>
        </div>
      )}

      {(screenshots || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>Screenshots</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {screenshots.map((s, i) => (
              s.url ? (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, overflow: 'hidden' }}
                >
                  <img
                    src={s.url}
                    alt={`Screenshot ${i + 1}`}
                    style={{ width: 96, height: 96, objectFit: 'cover', display: 'block' }}
                  />
                </a>
              ) : (
                <div key={i} style={{
                  width: 96, height: 96, background: 'rgba(255,255,255,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.6rem', color: '#666',
                }}>Image expired</div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Action area — depends on viewer + status */}
      {isDev && report.status === 'open' && (
        <div style={{
          marginBottom: 12,
          padding: 12,
          background: 'rgba(155,89,182,0.06)',
          border: '1px solid rgba(155,89,182,0.3)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.78rem', color: '#9b59b6', fontWeight: 600 }}>
            Dev controls
          </span>
          <button
            onClick={onAddressed}
            disabled={busy}
            style={{
              padding: '6px 14px',
              fontSize: '0.78rem',
              fontWeight: 600,
              background: 'rgba(155,89,182,0.2)',
              border: '1px solid #9b59b6',
              borderRadius: 5,
              color: '#d2a3e5',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            ✓ Mark as Addressed
          </button>
        </div>
      )}

      {isReporter && report.status === 'addressed' && !showNotFixed && (
        <div style={{
          marginBottom: 12,
          padding: 14,
          background: 'linear-gradient(135deg, rgba(120,220,140,0.10), rgba(39,201,90,0.04))',
          border: '1px solid rgba(120,220,140,0.4)',
          borderRadius: 8,
          boxShadow: '0 2px 12px rgba(39,201,90,0.15)',
        }}>
          <div style={{
            fontSize: '0.92rem',
            color: '#78dc8c',
            marginBottom: 4,
            fontWeight: 700,
            fontFamily: 'Georgia, "Times New Roman", serif',
          }}>
            ✨ Awaiting your confirmation
          </div>
          <div style={{ fontSize: '0.74rem', color: '#b0a48a', marginBottom: 12, lineHeight: 1.4 }}>
            The dev says this should be fixed in the live app. Please test and confirm —
            it stays open on your end until you do.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={onResolved}
              disabled={busy}
              style={{
                padding: '10px 18px',
                fontSize: '0.88rem',
                fontWeight: 700,
                background: 'linear-gradient(180deg, #27c95a 0%, #1c9c44 100%)',
                border: '1px solid #27c95a',
                borderRadius: 8,
                color: '#fff',
                cursor: busy ? 'wait' : 'pointer',
                boxShadow: '0 2px 8px rgba(39,201,90,0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: '1rem' }}>✓</span>
              Yes, mark resolved
            </button>
            <button
              onClick={() => setShowNotFixed(true)}
              disabled={busy}
              style={{
                padding: '10px 18px',
                fontSize: '0.78rem',
                fontWeight: 600,
                background: 'rgba(255,105,97,0.12)',
                border: '1px solid #ff6961',
                borderRadius: 8,
                color: '#ff6961',
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              ❌ This didn't fix it
            </button>
          </div>
        </div>
      )}

      {isReporter && report.status === 'addressed' && showNotFixed && (
        <div style={{
          marginBottom: 12,
          padding: 12,
          background: 'rgba(255,105,97,0.06)',
          border: '1px solid rgba(255,105,97,0.3)',
          borderRadius: 6,
        }}>
          <div style={{ fontSize: '0.78rem', color: '#ff6961', marginBottom: 6, fontWeight: 600 }}>
            What's still broken? (your note will reopen the report)
          </div>
          <textarea
            value={notFixedBody}
            onChange={e => setNotFixedBody(e.target.value)}
            rows={3}
            placeholder="Describe what's still happening…"
            style={{
              width: '100%',
              padding: 8,
              fontSize: '0.82rem',
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,105,97,0.3)',
              borderRadius: 5,
              color: '#e8d5b7',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              marginBottom: 8,
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onNotFixed}
              disabled={busy || !notFixedBody.trim()}
              style={{
                padding: '5px 12px', fontSize: '0.74rem',
                background: notFixedBody.trim() ? 'rgba(255,105,97,0.18)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${notFixedBody.trim() ? '#ff6961' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 5,
                color: notFixedBody.trim() ? '#ff6961' : '#666',
                cursor: busy ? 'wait' : (notFixedBody.trim() ? 'pointer' : 'not-allowed'),
                fontWeight: 600,
              }}
            >
              Reopen
            </button>
            <button
              onClick={() => { setShowNotFixed(false); setNotFixedBody(''); }}
              style={{
                padding: '5px 12px', fontSize: '0.74rem',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 5, color: '#888', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Comment thread */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>
          Discussion ({(comments || []).length})
        </div>
        {(comments || []).length === 0 ? (
          <div style={{ fontSize: '0.7rem', color: '#666', fontStyle: 'italic' }}>No comments yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {comments.map((c, i) => {
              const fromDev = c.authorRole === 'dev' || c.authorRole === 'dm';
              return (
                <div key={i} style={{
                  padding: 8,
                  background: fromDev ? 'rgba(155,89,182,0.06)' : 'rgba(74,144,217,0.06)',
                  border: `1px solid ${fromDev ? 'rgba(155,89,182,0.25)' : 'rgba(74,144,217,0.25)'}`,
                  borderRadius: 5,
                }}>
                  <div style={{ fontSize: '0.62rem', color: '#888', marginBottom: 3 }}>
                    <span style={{ color: fromDev ? '#9b59b6' : '#4a90d9', fontWeight: 600 }}>
                      {fromDev ? 'Dev' : 'Reporter'}
                    </span>
                    {' · '}{new Date(c.ts).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#e8d5b7', whiteSpace: 'pre-wrap' }}>{c.body}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <textarea
          value={reply}
          onChange={e => setReply(e.target.value)}
          placeholder="Reply…"
          rows={2}
          style={{
            flex: 1,
            padding: 6,
            fontSize: '0.78rem',
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(201,162,39,0.25)',
            borderRadius: 5,
            color: '#e8d5b7',
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
        />
        <button
          onClick={onReply}
          disabled={busy || !reply.trim()}
          style={{
            padding: '6px 12px',
            fontSize: '0.78rem',
            background: reply.trim() ? 'rgba(39,201,90,0.12)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${reply.trim() ? 'rgba(39,201,90,0.4)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 5,
            color: reply.trim() ? '#27c95a' : '#666',
            cursor: busy ? 'wait' : (reply.trim() ? 'pointer' : 'not-allowed'),
          }}
        >
          {busy ? '…' : 'Reply'}
        </button>
      </div>

      {/* Similar reports — only when there are matches */}
      {Array.isArray(similar) && similar.length > 0 && (
        <details style={{ marginTop: 16, fontSize: '0.74rem' }}>
          <summary style={{ cursor: 'pointer', color: '#888' }}>
            📎 {similar.length} similar report{similar.length === 1 ? '' : 's'}
          </summary>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {similar.map(s => (
              <button
                key={s.reportId}
                onClick={() => onOpenReport ? onOpenReport(s.reportId) : null}
                disabled={!onOpenReport}
                style={{
                  textAlign: 'left',
                  padding: '6px 10px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 5,
                  color: '#b0a48a',
                  cursor: onOpenReport ? 'pointer' : 'default',
                  fontSize: '0.72rem',
                }}
              >
                <span style={{
                  padding: '1px 6px',
                  borderRadius: 6,
                  background: `${(STATUS_INFO[s.status] || { color: '#888' }).color}22`,
                  color: (STATUS_INFO[s.status] || { color: '#888' }).color,
                  fontSize: '0.6rem',
                  marginRight: 6,
                }}>{s.status}</span>
                <strong>{s.title || '(untitled)'}</strong>
                <span style={{ color: '#666', marginLeft: 8, fontSize: '0.62rem' }}>
                  shared: {s.sharedTokens.slice(0, 4).join(', ')}
                </span>
              </button>
            ))}
          </div>
        </details>
      )}

      {/* Auto-attached context — collapsed */}
      <details style={{ marginTop: 14, fontSize: '0.7rem', color: '#888' }}>
        <summary style={{ cursor: 'pointer' }}>Auto-attached context</summary>
        <pre style={{
          fontSize: '0.6rem',
          padding: 8,
          background: 'rgba(0,0,0,0.3)',
          borderRadius: 4,
          overflow: 'auto',
          maxHeight: 240,
          color: '#888',
          margin: '6px 0 0 0',
        }}>
{JSON.stringify({
  appContext: parsed.appContext,
  characterSnapshot: parsed.characterSnapshot,
  recordedInteractions: parsed.recordedInteractions,
  lastAuditEvents: parsed.lastAuditEvents,
  breadcrumbs: parsed.breadcrumbs,
  elements: parsed.elements,
  statusHistory: parsed.statusHistory,
}, null, 2)}
        </pre>
      </details>
    </div>
  );
}
